import {type UIChild, UI} from "../../../../../stem-core/ui/UIBase";

import {extractMerchantEntries, iFrameUserDataService} from "../../../../services/IFrameUserDataService";
import {UserDashboardSection} from "../components/UserDashboardSection.jsx";
import {Messages} from "../../../../Messages";
import {iFrameMerchantService} from "../../../../services/IframeMerchantService";
import {UserDashboardTab} from "./UserDashboardTab.jsx";
import {UserDashboardPaymentsTable} from "../components/UserDashboardPaymentsTable.jsx";
import {Payment} from "../../../../../client/state/PaymentStore";
import {PaymentMethod} from "../../../../../client/state/PaymentMethodStore";
import {Router} from "../../../../../stem-core/ui/Router";
import {INLINE_EDIT_SUBSCRIPTION_URLS} from "../../../PanelConstants";
import {SubscriptionFlowType} from "../../../flows/steps/SubscriptionPlanFlowStep.js";
import {SubscriptionApplyPolicy, Subscription} from "../../../../../client/state/SubscriptionStore";
import {MessageElement} from "../../../../widget/ui/MessageElement";
import {autoredraw} from "../../../../../stem-core/decorators/AutoRedraw";
import {UserAddress} from "../../../../../client/state/UserAddressStore";
import {UsingBlinkDefaultPaymentMethod} from "../../../../common/UsingBlinkDefaultPaymentMethod";
import {BAFFLER_ALIAS, TNR_ALIAS} from "../../../../../blink-sdk/Constants";
import {UserDashboardCDSAccountSection} from "../components/UserDashboardCDSSection.jsx";
import {startSubscriptionFlow} from "../../../flows/FlowEntrypoint.js";
import {getPaymentMethodEmptySubscriptionMessage} from "../../../../common/MessagingUtils.js";
import {SubscriptionFlowHandler} from "../../../flows/SubscriptionFlowHandler.js";
import {BasePanelPage} from "../../../PanelPage.jsx";
import {iFrameState} from "../../../../services/IFrameState.js";
import {PaymentMethodDescription} from "../../../../common/PaymentMethodDescription.jsx";
import {Flow} from "../../../flows/Flow.js";
import {AddressDescription} from "../../panels/address/components/AddressDescription.jsx";
import {BlinkButton} from "../../../../../core/ui/Button.jsx";
import {userService} from "../../../../../client/connection/services/UserService.js";
import {Link} from "../../../../../stem-core/ui/primitives/Link.jsx";
import {logOutFromPanel, UserDashboardAccountTabStyle} from "./UserDashboardAccountTab.jsx";
import {registerStyle} from "../../../../../stem-core/ui/style/Theme";
import {LinkButton} from "../../../../../core/ui/LinkButton.jsx";


// TODO @cleanup rename this shit
@autoredraw(PaymentMethod, UserAddress, Subscription)
export class UserDashboardSubscriptionTab extends UserDashboardTab {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            title: Messages.inlineUserDashboardSubscription,
        }
    }

    async editPlan() {
        // TODO @flow2 looks like we can't get away from using a stack of flows
        Flow.activeFlowHandler = Flow.editSubscription;

        Flow.startPlanFromActiveHandler();
    }

    async editAddress() {
        Flow.editSubscription.getFlowPlan(); // Init only

        // Just use a couple of the whole steps
        const flowPlan = Flow.editSubscription.makeFlowPlan([
            Flow.address.update({
                shouldPrompt: () => true,
            }),
            Flow.editSubscriptionConfirmation, // Editing the address required a confirmation
        ]);

        Flow.startPlan(flowPlan);
    }

    async editPaymentMethod() {
        Flow.editSubscription.getFlowPlan(); // Init only

        await Flow.paymentMethod.prompt({
            continueLabel: Messages.updatePaymentMethod,
        });

        await Flow.editSubscription.finalize();
    }

    cancelSubscription(subscription: Subscription) {
        SubscriptionFlowHandler.subscriptionToCancel = subscription;
        Flow.subscriptionPlan.init({
            subscriptionFlowType: SubscriptionFlowType.EDIT_SUBSCRIPTION,
            selectedValue: null,
        });
        Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.cancel);
    }

    renderBafflerArchiveLink(subscription: Subscription) {
        if (!iFrameMerchantService.isMerchant(BAFFLER_ALIAS)) {
            return null;
        }

        const shippingAddress = subscription.getShippingAddress();
        if (!shippingAddress) {
            return null;
        }

        let url = "https://www.ezsubscription.com/webreader/#/login/baffler/general";

        // Fulco won't support the trailing "="
        function encode(str: string) {
            str = btoa(str);
            if (str.endsWith("=")) str = str.substring(0, str.length -1);
            if (str.endsWith("=")) str = str.substring(0, str.length -1);
            return str;
        }

        url += `?email=${encode(userService.getUserEmail())}`;

        if (shippingAddress.country === "United States") {
            url += `&zip=${encode(shippingAddress.postalCode)}`;
        } else {
            url += "&country=F";
        }

        return <UserDashboardSection titleMessage="Ebook Library">
            Access the ebook library by logging in with your email and zip code <Link newTab href={url.toString()} label="here"/>.
        </UserDashboardSection>
    }

    renderSubscription() {
        const {styleSheet} = this;
        const subscription = iFrameUserDataService.getActiveSubscription();

        if (!subscription) {
            return [
                <UserDashboardSection titleMessage={Messages.subscriptionPlanTitle} headerTitle={Messages.activeSubscription}>
                    <MessageElement
                        className={styleSheet.textSection}
                        message={Messages.notSubscribedToMerchant}
                        args={[iFrameMerchantService.getMerchant().name]}
                    />
                    <BlinkButton
                        label={Messages.subscribe}
                        className={styleSheet.button}
                        onClick={() => startSubscriptionFlow()}
                    />
                </UserDashboardSection>,
            ];
        }

        return this.renderActiveSubscription(subscription);
    }

    renderActiveSubscription(subscription: Subscription) {
        const {styleSheet} = this;
        const preferredPaymentMethod = subscription.getPaymentMethod();
        const planActions = [
            Messages.change,
            () => this.editPlan(),
        ];
        const paymentMethodActions = [
            Messages.change,
            () => this.editPaymentMethod(),
        ];

        return [
            <UserDashboardSection
                headerTitle={Messages.activeSubscription}
                titleMessage={Messages.subscriptionPlanTitle}
                action={planActions}
                hideSeparator
            >
                <MessageElement
                    message={Messages.subscribedCurrentPlan}
                    args={[subscription.coverage.name, subscription.formatPrice()]}
                    className={styleSheet.textSection}
                />
                {this.renderCancelButton(subscription)}
            </UserDashboardSection>,

            subscription.getShippingAddress() && <UserDashboardSection
                titleMessage={Messages.inlineAddressSectionTitle}
                action={[Messages.change, () => {
                    iFrameState.iframeParams.hasStepIndicator = false;
                    this.editAddress();
                }]}
                hideSeparator={true}
            >
                <AddressDescription userAddress={subscription.getShippingAddress()}/>
            </UserDashboardSection>,

            this.renderBafflerArchiveLink(subscription),

            <UserDashboardSection titleMessage={Messages.paymentMethodTitle} action={paymentMethodActions}>
                {
                    preferredPaymentMethod ?
                        <PaymentMethodDescription paymentMethod={subscription.getPaymentMethod()} /> : (
                            iFrameUserDataService.hasPrimaryPaymentMethod() ?
                                [
                                    <UsingBlinkDefaultPaymentMethod/>,
                                    // TODO: This message is not correct in some cases, make something less stupid!
                                    <MessageElement
                                        className={styleSheet.textSection}
                                        message={Messages.paymentMethodCharge}
                                        args={[subscription.getPrice(), subscription.billingPlan.getCycleDuration(true)]}
                                    />
                                ] :
                                <div>
                                    <MessageElement message={getPaymentMethodEmptySubscriptionMessage(subscription)} />
                                </div>
                        )
                }

            </UserDashboardSection>,
        ];
    }

    renderPaymentHistory() {
        return <UserDashboardSection headerTitle={Messages.paymentHistory}>
            <UserDashboardPaymentsTable resourceType={Payment.ResourceType.SUBSCRIPTION}/>
        </UserDashboardSection>;
    }

    renderCancelButton(subscription: Subscription) {
        return subscription.isActive() && !subscription.isActiveButCanceled() ?
            <div className={this.styleSheet.cancel} onClick={() => this.cancelSubscription(subscription)}>
                {Messages.cancelSubscription}
            </div> : null;
    }

    renderGiftedSubscriptions() {
        const {styleSheet} = this;
        const giftSentActiveSubscriptions = extractMerchantEntries(Subscription.getActive()).filter(s => s.conversion?.metadata?.gift && s.billedUserId === userService.getUserId());

        return giftSentActiveSubscriptions.map((giftSub, index) =>
        <UserDashboardSection
            titleMessage={Messages.subscriptionPlan}
            hideSeparator={index !== giftSentActiveSubscriptions.length - 1}
            {...(index ? {} : {headerTitle: Messages.giftSubscription})}
        >
            <MessageElement
                className={styleSheet.textSection}
                message={Messages.giftSubscriptionText}
                args={[giftSub.user.getEmail(), giftSub.coverage.name, giftSub.formatPrice()]}
            />
            {this.renderCancelButton(giftSub)}
        </UserDashboardSection>);
    }

    render(): UIChild {
        return [
            this.renderSubscription(),
            this.renderGiftedSubscriptions(),
            this.renderPaymentHistory(),
        ];
    }
}

// TNR manages subscriptions in the CDS Customer Care portal, so it only shows the subscription status, in place of
// both the subscription and account tabs
@registerStyle(UserDashboardAccountTabStyle)
export class TNRUserDashboardAccountTab extends UserDashboardSubscriptionTab {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            title: Messages.accountPage,
        };
    }

    renderActiveSubscription(subscription: Subscription) {
        return [
            <UserDashboardSection headerTitle={Messages.activeSubscription} titleMessage={Messages.subscriptionPlanTitle}>
                <MessageElement
                    className={this.styleSheet.textSection}
                    message={subscription.isActiveButCanceled() ? Messages.tnrSubscriptionNotRenewing : Messages.tnrSubscriptionActive}
                    args={[subscription.coverage.name, subscription.getActiveUntil().format("MMMM D, YYYY")]}
                />
            </UserDashboardSection>,
        ];
    }

    render() {
        const {styleSheet} = this;

        return [
            this.renderSubscription(),
            UserDashboardCDSAccountSection(),

            <UserDashboardSection titleMessage={Messages.email}>
                <div className={styleSheet.textSection}>
                    {userService.getUserEmail()}
                </div>
            </UserDashboardSection>,

            <div className={styleSheet.logOutLinkContainer}>
                <LinkButton label={Messages.logOut} onClick={() => logOutFromPanel()} />
            </div>,
        ];
    }
}

export class ManageSubscriptionPanel extends BasePanelPage {
    render() {
        if (iFrameMerchantService.isMerchant(TNR_ALIAS)) {
            return <TNRUserDashboardAccountTab />
        }
        return <UserDashboardSubscriptionTab />
    }
}
